import { useMemo, useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { Button, Card, Disclosure } from '@/components/UI';
import type { EntityTypeUnion } from '@einhaus/project314-types';
import type { MissingEntity } from './backtestStatusTypes';

interface MissingEntitiesPanelProps {
    missingEntities: MissingEntity[];
    missingEntitiesCount: number;
    canPopulate: boolean;
    isPopulating: boolean;
    expanded: boolean;
    onToggleExpanded: (next: boolean) => void;
    onPopulateAll: () => void;
    onPopulateEntity: (entity: { id: number; type: EntityTypeUnion['entityType']; symbol: string }) => void;
}

const VISIBLE_LIMIT = 50;

const entityTypeShortLabel = (type: EntityTypeUnion['entityType']): string => {
    if (type === 'Asset') return 'Stock';
    if (type === 'Cryptocurrency') return 'Crypto';
    if (type === 'AssetIndex') return 'Index';

    return type;
};

const MissingEntitiesPanel = ({
    missingEntities,
    missingEntitiesCount,
    canPopulate,
    isPopulating,
    expanded,
    onToggleExpanded,
    onPopulateAll,
    onPopulateEntity
}: MissingEntitiesPanelProps) => {
    const [search, setSearch] = useState('');

    const breakdown = useMemo(() => {
        const counts = new Map<EntityTypeUnion['entityType'], number>();

        for (const entity of missingEntities) {
            counts.set(entity.type, (counts.get(entity.type) ?? 0) + 1);
        }

        return [...counts.entries()];
    }, [missingEntities]);

    const filteredEntities = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) return missingEntities;

        return missingEntities.filter(
            (entity) => (entity.symbol ?? '').toLowerCase().includes(normalizedSearch) || String(entity.id).includes(normalizedSearch)
        );
    }, [missingEntities, search]);

    const visibleEntities = filteredEntities.slice(0, VISIBLE_LIMIT);

    if (missingEntitiesCount === 0) {
        return (
            <Card>
                <div className="flex items-center gap-2 p-4 text-sm text-green-400">
                    <FaCheckCircle aria-hidden="true" />
                    All entities have at least one backtest. See Population above for complete combination coverage.
                </div>
            </Card>
        );
    }

    return (
        <Disclosure
            badge={<span className="rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300">{missingEntitiesCount}</span>}
            expanded={expanded}
            headerActions={
                <Button
                    disabled={!canPopulate || isPopulating}
                    isLoading={isPopulating}
                    size="sm"
                    variant="primary"
                    onClick={onPopulateAll}
                >
                    Populate all
                </Button>
            }
            title="Missing entities"
            onToggle={onToggleExpanded}
        >
            <div className="mb-3 flex flex-wrap items-center gap-2">
                {breakdown.map(([type, count]) => (
                    <span className="rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300" key={type}>
                        {entityTypeShortLabel(type)} {count}
                    </span>
                ))}
                <input
                    className="ml-auto w-40 rounded-md border border-zinc-700 bg-black/30 px-2 py-1 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-cyan-600 focus:outline-none"
                    placeholder="Filter by symbol or ID"
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </div>

            <div className="max-h-80 overflow-y-auto">
                <ul className="divide-y divide-zinc-800">
                    {visibleEntities.map((entity) => (
                        <li className="flex items-center justify-between gap-2 py-2" key={`${entity.id}-${entity.type}`}>
                            <span className="text-sm text-zinc-200">
                                {entity.symbol || 'Unknown'}{' '}
                                <span className="text-xs text-zinc-500">
                                    {entityTypeShortLabel(entity.type)} · {entity.id}
                                </span>
                            </span>
                            <Button
                                disabled={!canPopulate}
                                size="sm"
                                variant="outline-dark"
                                onClick={() => onPopulateEntity({ id: entity.id, type: entity.type, symbol: entity.symbol || 'Unknown' })}
                            >
                                Populate
                            </Button>
                        </li>
                    ))}
                    {visibleEntities.length === 0 && <li className="py-3 text-xs text-zinc-500">No entities match “{search}”.</li>}
                </ul>
                {filteredEntities.length > VISIBLE_LIMIT && (
                    <div className="mt-2 text-xs text-zinc-500">
                        Showing first {VISIBLE_LIMIT} of {filteredEntities.length} matching entities.
                    </div>
                )}
            </div>
        </Disclosure>
    );
};

export default MissingEntitiesPanel;
